<template>
  <div class="bsp-container bsp-page">
    <div class="content bsp-page__content" v-if="!loading && whatsappBusinessAccount">
      <BspPageHeader
        :breadcrumbs="accountBreadcrumbs"
        :title="whatsappBusinessAccount.name"
        description="Manage the phone numbers, settings, and metrics for this account."
        eyebrow="WhatsApp Business Account"
        icon="tabler:brand-whatsapp"
      >
        <template #actions>
          <AButton
            variant="outlined"
            prepend-icon="tabler:shopping-bag"
            @click="goToProductCatalogs"
          >
            Product catalogs
          </AButton>
          <AButton
            v-if="isSuperUser"
            variant="outlined"
            prepend-icon="tabler:credit-card"
            @click="geToCreditLine"
          >
            Credit line
          </AButton>
          <AButton
            color="blue"
            prepend-icon="tabler:refresh"
            @click="synchronizePhoneNumbers"
          >
            Synchronize phone numbers
          </AButton>
        </template>
        <template #meta>
          <ATag :text="`WABA ${whatsappBusinessAccount.id}`" color="info" type="secondary" />
        </template>
      </BspPageHeader>

      <section class="bsp-summary-grid" aria-label="Account summary">
        <AInformationCard
          title="Owning business"
          :description="whatsappBusinessAccount.owner_business_info.name"
          icon-name="tabler:building"
          variant="flat"
        />
        <AInformationCard
          title="Business ID"
          :description="whatsappBusinessAccount.owner_business_info.id"
          icon-name="tabler:fingerprint"
          variant="flat"
        />
        <AInformationCard
          title="Connected phone numbers"
          :description="phoneNumbersLoaded ? `${phoneNumbers.length} phone numbers` : 'Loading…'"
          icon-name="tabler:device-mobile"
          variant="flat"
        />
      </section>

      <section class="bsp-section phone-numbers" aria-labelledby="phone-numbers-title">
        <div class="bsp-section-heading bsp-section-heading--toolbar">
          <div>
            <div class="bsp-section-heading__title-row">
              <h2 id="phone-numbers-title">Phone numbers</h2>
              <ATag
                v-if="phoneNumbersLoaded"
                :text="`${phoneNumbers.length}`"
                color="primary"
                type="secondary"
              />
            </div>
            <p>Select a phone number to manage webhooks, business profile, and templates.</p>
          </div>
          <AButton color="blue" prepend-icon="tabler:plus" @click="openAddPhoneNumberModal">
            Add phone number
          </AButton>
        </div>

        <div class="bsp-phone-grid" v-if="phoneNumbersLoaded && phoneNumbers.length">
          <v-card
            class="bsp-phone-card"
            variant="flat"
            v-for="phoneNumber in phoneNumbers"
            :key="phoneNumber.id"
            tabindex="0"
            @click="goToPhoneNumber(phoneNumber.id)"
            @keydown.enter="goToPhoneNumber(phoneNumber.id)"
          >
            <AIcon icon="tabler:phone" color="primary" size="20" with-container />
            <div class="phone-number-info">
              <h3>{{ phoneNumber.verified_name }}</h3>
              <span>{{ phoneNumber.display_phone_number }}</span>
            </div>
            <AButton
              icon="tabler:chevron-right"
              variant="text"
              size="small"
              help-text="Manage phone number"
              @click.stop="goToPhoneNumber(phoneNumber.id)"
            />
          </v-card>
        </div>
        <AEmptyViewCard
          v-else-if="phoneNumbersLoaded"
          icon="tabler:device-mobile-off"
          title="This account has no phone numbers"
          description="Add a phone number to start configuring it."
          action-button-label="Add phone number"
          @action="openAddPhoneNumberModal"
        />
        <div v-else class="bsp-inline-loader">
          <SpinnerLoader />
          <span>Loading phone numbers…</span>
        </div>
      </section>

      <section class="bsp-section metrics-section" v-if="isSuperUser" aria-labelledby="metrics-title">
        <div class="bsp-section-heading">
          <div>
            <span class="bsp-section-heading__eyebrow">Analytics</span>
            <h2 id="metrics-title">Account metrics</h2>
            <p>Filter activity by date range and phone numbers before generating the report.</p>
          </div>
        </div>

        <div class="bsp-filter-grid">
          <ASelector
            v-model="filters.analyticsType"
            :items="analyticsOptions"
            item-title="title"
            item-value="value"
            label="Analytics type"
          />
          <div class="bsp-native-field">
            <label for="startDate">From</label>
            <input type="date" id="startDate" v-model="filters.startDate">
          </div>
          <div class="bsp-native-field">
            <label for="endDate">To</label>
            <input type="date" id="endDate" v-model="filters.endDate">
          </div>
          <ASelector
            v-model="filters.phoneNumbers"
            :items="phoneNumberOptions"
            item-title="label"
            item-value="value"
            label="Phone numbers"
            placeholder="Select phone numbers"
            multiple
            return-object
            chips
            closable-chips
            :multiple-max-selection-show="2"
          />
          <div class="bsp-filter-actions">
            <AButton color="blue" :loading="loadingMetrics" prepend-icon="tabler:filter" @click="fetchMetrics">
              Apply filters
            </AButton>
            <AButton variant="outlined" prepend-icon="tabler:download" :loading="loadingMetrics" @click="downloadReport">
              Download report
            </AButton>
          </div>
        </div>

        <div class="bsp-table-shell" v-if="aggregatedMetrics.length > 0">
          <table>
            <thead>
              <tr>
                <th>Phone number</th>
                <th v-if="filters.analyticsType === 'conversation'">Conversations</th>
                <th v-if="filters.analyticsType === 'pricing'">Messages</th>
                <th v-if="filters.analyticsType === 'call'">Calls</th>
                <th v-if="filters.analyticsType === 'call'">Average duration</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(metric, index) in aggregatedMetrics" :key="index">
                <td>{{ metric.phone_number }}</td>
                <td v-if="filters.analyticsType === 'conversation'">{{ formatNumber(metric.conversation) }}</td>
                <td v-if="filters.analyticsType === 'pricing'">{{ formatNumber(metric.volume) }}</td>
                <td v-if="filters.analyticsType === 'call'">{{ formatNumber(metric.count) }}</td>
                <td v-if="filters.analyticsType === 'call'">{{ formatDuration(metric.average_duration) }}</td>
                <td>{{ formatCost(metric.cost) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td v-if="filters.analyticsType === 'conversation'"><strong>{{ formatNumber(totalConversations) }}</strong></td>
                <td v-if="filters.analyticsType === 'pricing'"><strong>{{ formatNumber(totalVolume) }}</strong></td>
                <td v-if="filters.analyticsType === 'call'"><strong>{{ formatNumber(totalCalls) }}</strong></td>
                <td v-if="filters.analyticsType === 'call'"><strong>{{ formatDuration(totalAverageDuration) }}</strong></td>
                <td><strong>{{ formatCost(totalCost) }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <AEmptyViewCard
          v-else-if="!loadingMetrics"
          icon="tabler:chart-bar-off"
          title="No metrics to display"
          description="Adjust the filters and try again."
        />
      </section>

      <details class="bsp-advanced-section">
        <summary>
          <span>
            <strong>Advanced tools</strong>
            <small>Administrative actions for Meta and the WABA.</small>
          </span>
          <AIcon icon="tabler:chevron-down" size="18" />
        </summary>
        <div class="bsp-advanced-section__content">
          <AButton variant="outlined" prepend-icon="tabler:refresh" @click="synchronizePhoneNumbers">
            Synchronize phone numbers
          </AButton>
          <AButton v-if="isSuperUser" variant="outlined" @click="getWabaSystemUsers">
            Get system users
          </AButton>
          <AButton v-if="isSuperUser" variant="outlined" @click="createSystemUser">
            Create system user
          </AButton>
          <AButton v-if="isSuperUser" variant="outlined" @click="subscribeApp">
            Subscribe app
          </AButton>
          <AButton v-if="isSuperUser" variant="outlined" @click="getSubscribedApps">
            Get subscribed apps
          </AButton>
          <pre v-if="isSuperUser && subcribedAppsResponse">{{ subcribedAppsResponse }}</pre>
        </div>
      </details>
    </div>
    <SpinnerLoader v-else />

    <AModal
      v-model="isAddPhoneNumberModalOpen"
      :title="isVerificationStep ? 'Verify phone number' : 'Add phone number'"
      prepend-title-icon="tabler:device-mobile-plus"
      width="560px"
      :close-disabled="loading"
      @on-close="closeAddPhoneNumberModal"
    >
      <template #body>
        <form v-if="!isVerificationStep" class="bsp-modal-form" @submit.prevent="createPhoneNumber">
          <p>Enter the information Meta will use to create the phone number.</p>
          <div class="bsp-form-grid bsp-form-grid--phone">
            <AInput
              v-model="phoneNumberForm.cc"
              type="tel"
              label="Country code"
              placeholder="56"
              autocomplete="tel-country-code"
              :pattern="numericPhoneIdentifierPattern"
              required
            />
            <AInput
              v-model="phoneNumberForm.phone_number"
              type="tel"
              label="Phone number"
              placeholder="912345678"
              autocomplete="tel-national"
              :pattern="numericPhoneIdentifierPattern"
              required
            />
          </div>
          <AInput v-model="phoneNumberForm.verified_name" label="Verified name" placeholder="My business" required />
          <p class="bsp-help-text">
            The name must comply with
            <a href="https://web.facebook.com/business/help/757569725593362" target="_blank" rel="noopener noreferrer">Meta's policies</a>.
          </p>
        </form>
        <form v-else class="bsp-modal-form" @submit.prevent="verifyPhoneNumber">
          <p>Enter the PIN Meta sent to the phone number.</p>
          <AInput v-model="pinCode" label="PIN code" placeholder="000000" required />
          <div class="resend-pin">
            <AButton type="button" variant="text" size="small" :disabled="isResendPinDisabled" @click="resendPin">
              Resend PIN
            </AButton>
            <p v-if="isResendPinDisabled">Available in {{ resendPinTimer }} seconds.</p>
          </div>
        </form>
      </template>
      <template #actions>
        <AButton variant="text" :disabled="loading" @click="closeAddPhoneNumberModal">Cancel</AButton>
        <AButton
          v-if="!isVerificationStep"
          color="blue"
          :loading="loading"
          :disabled="!isCreateButtonEnabled"
          @click="createPhoneNumber"
        >
          Create phone number
        </AButton>
        <AButton
          v-else
          color="blue"
          :loading="loading"
          :disabled="!isVerifyButtonEnabled"
          @click="verifyPhoneNumber"
        >
          Verify
        </AButton>
      </template>
    </AModal>
  </div>
</template>

<script setup lang="ts">

import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService/AuthService'
import { reactive, onMounted, ref, computed, watch } from 'vue'
import SpinnerLoader from '../components/loaders/SpinnerLoader.vue'
import { User } from '../services/AuthService/types'
import { Establishment } from '../services/EstablishmentService/types'
import { WhatsappBusinessAccount, PhoneNumber } from '../services/AccountService/types'
import { POSITION, useToast } from 'vue-toastification'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import AccountService from '@/services/AccountService/AccountService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import {
  AButton,
  AEmptyViewCard,
  AIcon,
  AInformationCard,
  AInput,
  AModal,
  ASelector,
  ATag
} from '@adereso-ai/vue-components'
import {
  aggregateMetrics as aggregateMetricsByPhone,
  buildAnalyticsParams,
  computeTotalAverageDuration,
  downloadAnalyticsReport,
  fetchAnalytics,
  filterMetrics,
  formatDuration,
  getReportFilename as resolveReportFilename,
  type AnalyticsReportParams,
  type AnalyticsType
} from '@/utils/metricsAnalytics'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const phoneNumbersLoaded = ref(false)
const currentUser = ref<User | undefined>(undefined)
const currentEstablishment = ref<Establishment | undefined>(undefined)
// const establishmentName = ref('')
const whatsappBusinessAccount = ref<WhatsappBusinessAccount | undefined>(undefined)

const phoneNumbers = ref<PhoneNumber[]>([])
const subcribedAppsResponse = ref<any>(undefined)

const phoneNumberOptions = computed(() => phoneNumbers.value.map((phone) => ({
  value: phone.clean_display_phone_number,
  label: phone.display_phone_number
})))

const analyticsOptions = [
  { title: 'Per message', value: 'pricing' },
  { title: 'Conversations', value: 'conversation' },
  { title: 'Calls', value: 'call' }
]

const accountBreadcrumbs = computed(() => [
  {
    title: 'WhatsApp Accounts',
    icon: 'tabler:brand-whatsapp',
    to: { name: 'whatsapp-business-accounts' }
  },
  {
    title: whatsappBusinessAccount.value?.name || 'Account',
    disabled: true
  }
])

const isAddPhoneNumberModalOpen = ref(false)
const isVerificationStep = ref(false)
const phoneNumberForm = ref({
  cc: '',
  phone_number: '',
  verified_name: ''
})
const numericPhoneIdentifierPattern = /^\d+$/
const pinCode = ref('')
const isCreateButtonEnabled = computed(() => {
  return numericPhoneIdentifierPattern.test(phoneNumberForm.value.cc.trim()) &&
    numericPhoneIdentifierPattern.test(phoneNumberForm.value.phone_number.trim()) &&
    phoneNumberForm.value.verified_name.trim().length > 0
})
const isVerifyButtonEnabled = computed(() => {
  return pinCode.value.trim().length > 0
})
const phoneId = ref('')
const isResendPinDisabled = ref(true)
const resendPinTimer = ref(60)
let resendPinInterval: any = null

const isSuperUser = computed(() => {
  return currentUser.value?.roles.includes('superuser')
})

const metrics = ref<any[]>([])
const filteredMetrics = ref<any[]>([])
const loadingMetrics = ref(false)
const aggregatedMetrics = ref<any[]>([])

const filters = reactive({
  analyticsType: 'pricing' as AnalyticsType,
  startDate: '',
  endDate: '',
  phoneNumbers: [] as { value: string, label: string }[]
})

const totalConversations = computed(() => {
  return aggregatedMetrics.value.reduce(
    (sum, metric) => sum + (metric.conversation ?? 0),
    0
  )
})

const totalVolume = computed(() => {
  return aggregatedMetrics.value.reduce(
    (sum, metric) => sum + (metric.volume ?? 0),
    0
  )
})

const totalCalls = computed(() => {
  return aggregatedMetrics.value.reduce(
    (sum, metric) => sum + (metric.count ?? 0),
    0
  )
})

const totalAverageDuration = computed(() => {
  return computeTotalAverageDuration(aggregatedMetrics.value)
})

const totalCost = computed(() => {
  return aggregatedMetrics.value.reduce((sum, metric) => sum + metric.cost, 0)
})

function applyFilters () {
  filteredMetrics.value = filterMetrics(metrics.value, filters)
  aggregatedMetrics.value = aggregateMetricsByPhone(filteredMetrics.value, filters.analyticsType)
}

function getReportFilename () {
  return resolveReportFilename(filters.analyticsType)
}

function cleanPinCode (value: string) {
  return value.replace(/\D/g, '') // Solo números
}

watch(pinCode, (newValue: string) => {
  pinCode.value = cleanPinCode(newValue)
})

function openAddPhoneNumberModal () {
  isAddPhoneNumberModalOpen.value = true
}

function closeAddPhoneNumberModal () {
  isAddPhoneNumberModalOpen.value = false
  resetAddPhoneNumberModal()
}

function resetAddPhoneNumberModal () {
  isVerificationStep.value = false
  phoneNumberForm.value = {
    cc: '',
    phone_number: '',
    verified_name: ''
  }
  pinCode.value = ''
  phoneId.value = ''
  isResendPinDisabled.value = true
  resendPinTimer.value = 60
  clearInterval(resendPinInterval)
}

async function createPhoneNumber () {
  if (whatsappBusinessAccount.value) {
    loading.value = true
    const metaService = await MetaService.getInstance()
    const payload = {
      cc: phoneNumberForm.value.cc.trim(),
      phone_number: phoneNumberForm.value.phone_number.trim(),
      verified_name: phoneNumberForm.value.verified_name.trim()
    }
    try {
      const response = await metaService.createPhoneNumber(whatsappBusinessAccount.value.id, payload)
      phoneId.value = response.phone_id
      toastSuccess('Phone number created successfully')
      isVerificationStep.value = true
      startResendPinTimer()
    } catch (error) {
      toastError('Error creating phone number: ' + error)
      closeAddPhoneNumberModal()
    }
    loading.value = false
  }
}

async function verifyPhoneNumber () {
  if (phoneId.value && whatsappBusinessAccount.value) {
    loading.value = true
    const metaService = await MetaService.getInstance()
    try {
      await metaService.verifyAndRegisterPhone(phoneId.value, pinCode.value)
      toastSuccess('Phone number verified successfully')
      closeAddPhoneNumberModal()
      await synchronizePhoneNumbers()
    } catch (error) {
      toastError('Error verifying phone number: ' + error)
    }
    loading.value = false
  }
}

function startResendPinTimer () {
  isResendPinDisabled.value = true
  resendPinTimer.value = 60
  resendPinInterval = setInterval(() => {
    resendPinTimer.value--
    if (resendPinTimer.value <= 0) {
      isResendPinDisabled.value = false
      clearInterval(resendPinInterval)
    }
  }, 1000)
}

async function resendPin () {
  if (phoneId.value && whatsappBusinessAccount.value) {
    loading.value = true
    const metaService = await MetaService.getInstance()
    try {
      await metaService.requestPhonePin(whatsappBusinessAccount.value.id, phoneId.value)
      toastSuccess('PIN code resent successfully')
      startResendPinTimer()
    } catch (error) {
      toastError('Error resending PIN code: ' + error)
    }
    loading.value = false
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    const establishment = await establishmentService.getCurrentEstablishment()
    currentEstablishment.value = establishment
  } catch (error) {
    // do nothing
  }
}

async function goToPhoneNumber (phoneNumberId: string) {
  router.push({ name: 'phone-number', params: { phoneNumberId } })
}

async function goToProductCatalogs () {
  if (!whatsappBusinessAccount.value) {
    return
  }
  router.push({ name: 'product-catalogs', params: { wabaId: whatsappBusinessAccount.value.id } })
}

async function geToCreditLine () {
  router.push({ name: 'credit-line', params: { wabaId: whatsappBusinessAccount.value?.id } })
}

async function createSystemUser () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const metaService = await MetaService.getInstance()
    try {
      const response = await metaService.createSystemUser(
        whatsappBusinessAccount.value.id,
        '122105581976177242'
      )
      toastSuccess('System user created' + JSON.stringify(response))
    } catch (error) {
      toastError('Error creating system user')
    }
  }
  loading.value = false
}

async function getSubscribedApps () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const metaService = await MetaService.getInstance()
    try {
      const response = await metaService.getSubscribedApps(whatsappBusinessAccount.value.id)
      subcribedAppsResponse.value = response
      toastSuccess('Subscribed apps updated' + JSON.stringify(response))
    } catch (error) {
      toastError('Error updating subscribed apps')
    }
  }
  loading.value = false
}

async function subscribeApp () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const metaService = await MetaService.getInstance()
    try {
      const response = await metaService.subscribeApp(whatsappBusinessAccount.value.id)
      toastSuccess('App subscribed' + JSON.stringify(response))
    } catch (error) {
      toastError('Error subscribing app')
    }
  }
  loading.value = false
}

async function getWabaSystemUsers () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const metaService = await MetaService.getInstance()
    try {
      const response = await metaService.getWabaSystemUsers(whatsappBusinessAccount.value.id)
      toastSuccess('System users updated' + JSON.stringify(response))
    } catch (error) {
      toastError('Error updating system users')
    }
  }
  loading.value = false
}

async function synchronizePhoneNumbers () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const metaService = await MetaService.getInstance()
    try {
      await metaService.synchronizeAccountPhoneNumbers(whatsappBusinessAccount.value.id)
      toastSuccess('Phone numbers synchronized')
      await getPhoneNumbers(whatsappBusinessAccount.value.id)
    } catch (error) {
      toastError('Error synchronizing phone numbers')
    }
  }
  loading.value = false
}

/* async function deleteAccount () {
  loading.value = true
  if (whatsappBusinessAccount.value) {
    const accountService = await AccountService.getInstance()
    try {
      await accountService.deleteWhatsappBusinessAccount(whatsappBusinessAccount.value.id)
      toastSuccess('Account deleted')
      goBack()
    } catch (error) {
      toastError('Error deleting account: ' + error)
    }
  }
  loading.value = false
} */

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (authService.currentUser.value == null) {
    router.push({ name: 'login', query: route.query })
  }
}

async function getWhatsappBusinessAccount (wabaId: string) {
  try {
    const accountService = await AccountService.getInstance()
    whatsappBusinessAccount.value = await accountService.getWhatsappBusinessAccount(wabaId)
  } catch {
    toastError('Error getting whatsapp business account')
    router.push({ name: 'home' })
  }
}

async function getPhoneNumbers (wabaId: string) {
  try {
    const accountService = await AccountService.getInstance()
    phoneNumbers.value = await accountService.getWhatsappBusinessAccountPhoneNumbers(wabaId)
    filters.phoneNumbers = phoneNumbers.value.map(phone => ({
      value: phone.clean_display_phone_number,
      label: phone.display_phone_number
    }))
  } catch {
    toastError('Error getting whatsapp business account')
  }
}

async function downloadReport () {
  if (!whatsappBusinessAccount.value) return

  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters, true) as AnalyticsReportParams
    const response = await downloadAnalyticsReport(
      filters.analyticsType,
      metaService,
      whatsappBusinessAccount.value.id,
      params
    )
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', getReportFilename())
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toastSuccess('Report downloaded successfully')
  } catch (error) {
    toastError('Error downloading report: ' + error)
  }
  loadingMetrics.value = false
}

onMounted(async () => {
  await router.isReady()
  await getCurrentUser()
  await getEstablishment()
  const wabaId = route.params.wabaId as string

  if (wabaId) {
    await getWhatsappBusinessAccount(wabaId)
    await getPhoneNumbers(wabaId)
    await fetchMetrics()
    phoneNumbersLoaded.value = true
    loading.value = false
  } else {
    router.push({ name: 'home' })
  }
})

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

function toastSuccess (successMessage: string) {
  try {
    toast.success(String(successMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

async function fetchMetrics () {
  if (!whatsappBusinessAccount.value) return

  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters)
    const response = await fetchAnalytics(
      filters.analyticsType,
      metaService,
      whatsappBusinessAccount.value.id,
      params
    )
    metrics.value = response.data_points
    applyFilters()
  } catch (error) {
    toastError('Error fetching metrics: ' + error)
  }
  loadingMetrics.value = false
}
function formatCost (value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}
function formatNumber (value) {
  return new Intl.NumberFormat('en-US').format(value)
}
</script>

<style scoped lang="scss">
/* Variables de Color */
$primary-color: #0052CC;
$secondary-color: #6B778C;
$success-color: #36B37E;
$danger-color: #FF5630;
$whatsapp-color: #25D366;
$background-color: #F4F5F7;
$text-color: #172B4D;
$border-color: #DFE1E6;

/* Importar Font Awesome */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

.bsp-container {
  padding: 20px;
  background-color: $background-color;
  color: $text-color;
  min-height: 100vh;

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 30px;

    .btn {
      margin-right: 20px;
      display: flex;
      align-items: center;

      i {
        margin-right: 5px;
      }
    }

    h1 {
      margin: 0;
      font-size: 28px;
    }

    .account-id {
      margin-left: auto;
      font-size: 14px;
      color: $secondary-color;
    }
  }

  .content {
    max-width: 1000px;
    margin: 0 auto;

    .account-details {
      background-color: #fff;
      border: 1px solid $border-color;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;

      .owner-info {
        margin-bottom: 30px;

        h2 {
          margin-top: 0;
          border-bottom: 1px solid $border-color;
          padding-bottom: 10px;
        }

        p {
          margin: 5px 0;
        }
      }

      .phone-numbers {
        margin-bottom: 30px;

        h2 {
          border-bottom: 1px solid $border-color;
          padding-bottom: 10px;
          margin-top: 0;
        }

        .phone-number-list {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-top: 20px;

          .phone-number-item {
            background-color: #fff;
            border: 1px solid $border-color;
            border-radius: 6px;
            padding: 15px;
            width: calc(33.333% - 10px);
            box-sizing: border-box;
            cursor: pointer;
            display: flex;
            align-items: center;
            transition: background-color 0.2s;

            &:hover {
              background-color: $background-color;
            }

            i {
              font-size: 24px;
              color: $primary-color;
              margin-right: 15px;
            }

            .phone-number-info {
              .phone-name {
                font-size: 16px;
                font-weight: bold;
                margin: 0;
              }

              .phone-number {
                font-size: 14px;
                color: $secondary-color;
                margin: 0;
              }
            }
          }
        }

        .btn {
          margin-top: 20px;
          display: flex;
          align-items: center;

          i {
            margin-right: 5px;
          }
        }
      }

      .subscribed-apps {
        h2 {
          border-bottom: 1px solid $border-color;
          padding-bottom: 10px;
          margin-top: 0;
        }

        pre {
          background-color: #F1F3F5;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid $border-color;
          max-height: 300px;
          overflow: auto;
          font-size: 14px;
          color: $text-color;
        }
      }
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-bottom: 20px;

      .btn {
        display: flex;
        align-items: center;

        i {
          margin-right: 5px;
        }
      }
    }

    .api-actions {
      background-color: #fff;
      border: 1px solid $border-color;
      border-radius: 8px;
      padding: 20px;

      h3 {
        margin-top: 0;
        border-bottom: 1px solid $border-color;
        padding-bottom: 10px;
        margin-bottom: 20px;
      }

      .api-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .btn {
          display: flex;
          align-items: center;
          padding: 10px 15px;

          i {
            margin-right: 5px;
          }
        }
      }
    }
  }

  /* Estilos del Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background-color: #fff;
    padding: 30px 20px;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    position: relative;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);

    .close-button {
      position: absolute;
      top: 15px;
      right: 15px;
      font-size: 28px;
      font-weight: bold;
      color: $secondary-color;
      cursor: pointer;

      &:hover {
        color: $text-color;
      }
    }

    h3 {
      margin-top: 0;
      margin-bottom: 20px;
    }

    form {
      .form-group {
        margin-bottom: 15px;

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }

        input {
          width: 100%;
          padding: 8px;
          box-sizing: border-box;
          border: 1px solid $border-color;
          border-radius: 4px;
        }
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;

        .btn {
          padding: 10px 20px;
        }
      }
    }

    .policies-text {
      font-size: 12px;
      color: $secondary-color;

      a {
        color: $primary-color;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .resend-pin {
      margin-top: 20px;
      text-align: right;

      .btn-link {
        padding: 0;
        background: none;
        border: none;
        color: $primary-color;
        cursor: pointer;

        &:disabled {
          color: $secondary-color;
          cursor: not-allowed;
        }
      }

      p {
        font-size: 12px;
        color: $secondary-color;
        margin-top: 5px;
      }
    }
  }
}

/* Estilos para Botones */
.btn {
  display: inline-block;
  font-weight: 500;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  padding: 0.5rem 1rem;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 4px;
  border: 1px solid transparent;
  color: #fff;
  background-color: $primary-color;
  border-color: $primary-color;
  text-decoration: none;

  &:hover {
    background-color: darken($primary-color, 5%);
    border-color: darken($primary-color, 5%);
  }

  &.btn-secondary {
    background-color: $secondary-color;
    border-color: $secondary-color;

    &:hover {
      background-color: darken($secondary-color, 5%);
      border-color: darken($secondary-color, 5%);
    }
  }

  &.btn-success {
    background-color: $success-color;
    border-color: $success-color;

    &:hover {
      background-color: darken($success-color, 5%);
      border-color: darken($success-color, 5%);
    }
  }

  &.btn-danger {
    background-color: $danger-color;
    border-color: $danger-color;

    &:hover {
      background-color: darken($danger-color, 5%);
      border-color: darken($danger-color, 5%);
    }
  }

  &.btn-whatsapp {
    background-color: $whatsapp-color;
    border-color: $whatsapp-color;

    &:hover {
      background-color: darken($whatsapp-color, 5%);
      border-color: darken($whatsapp-color, 5%);
    }
  }

  &.btn-link {
    background: none;
    border: none;
    color: $primary-color;
    padding: 0;
    font-weight: normal;

    &:hover {
      color: darken($primary-color, 5%);
      text-decoration: underline;
    }

    &:disabled {
      color: $secondary-color;
      cursor: not-allowed;
    }
  }
}

.metrics-section {
  background-color: #fff;
  border: 1px solid $border-color;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  margin-bottom: 20px;

  h2 {
    margin-top: 0;
    border-bottom: 1px solid $border-color;
    padding-bottom: 10px;
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;

    .filter-item {
      display: flex;
      flex-direction: column;

      label {
        font-weight: bold;
        margin-bottom: 5px;
      }

      input, select {
        padding: 5px;
        border: 1px solid $border-color;
        border-radius: 4px;
      }
    }

    .btn {
      align-self: flex-end;
      margin-left: auto;
    }
  }

  .metrics-display {
    table {
      width: 100%;
      border-collapse: collapse;

      th, td {
        border: 1px solid $border-color;
        padding: 8px;
        text-align: left;
      }

      th {
        background-color: $background-color;
      }
    }
  }
}
</style>
